// @ts-nocheck
import React, { useEffect, useState } from 'react'
import { endpoints } from '../../utils/Constants';
import { Button, Badge, Spinner } from 'reactstrap';
import { saveAs } from 'file-saver';
import useFetchSimple from '../../Services/useFetchSimple';
import UploadDropZone from '../Form/UploadDropZone'
import dayjs from 'dayjs'
import { showDate, showDateTime } from '../../utils/Helpers';
import { useAlert } from '../../Services/AlertContext';
import { useAuth } from '../../Services/AuthContext';
import SupplierDetail from '../Cards/SupplierDetail';
import DatePickerControl from '../Form/DatePicker';
import { useCallback } from 'react';
import { ISupplierDocument } from '../../Models/ISupplierDocument';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faCheck, faBan } from '@fortawesome/free-solid-svg-icons';
import { ISupplierDocumentUploadPolicy } from '../../Models/ISupplierDocumentUploadPolicy';
import './DocumentDetails.css';


const DocumentDetails = ({ optional, index, route, doc, refresh, supplierId, uploadPolicy }: { optional?: true, index?: string, route?: string, doc: ISupplierDocument, refresh: () => void, supplierId: string, uploadPolicy?: ISupplierDocumentUploadPolicy }) => {


    const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
        const byteCharacters = atob(b64Data);
        const byteArrays: Uint8Array[] = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: contentType });
        return blob;
    }
    const blobToBase64 = file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });

    const [newFile, setFile] = useState();

    const { fileName, fileCategory, lastChecked, checkedByUser, expiredAt, status, id } = doc;
    const approved = status === 'Current' || status === 'Nearly Expired';
    const expired = status === "Expired";

    const [approving, setApproving] = useState();
    const [expireDate, setExpire] = useState<void | Date>();
    const [uploading, setUploading] = useState(false);
    const fetch = useFetchSimple();
    const [gettingDocument, setGettingDocument] = useState(false);
    const alert = useAlert();
    const [auth] = useAuth();

    const downloadDoc = async () => {
        setGettingDocument(true);
        try {
            const result = await fetch(endpoints.getSupplierDocument + id);
            if (!result?.body?.file) {
                alert({ title: 'Error downloading document', body: <p>Document content was unavailable. Please try again later.</p> });
                return;
            }

            const extension = (fileName || '').split('.').pop()?.toLowerCase() || '';
            const mimeType = Object.entries(uploadPolicy?.mimeTypes || {}).find(([key]) => key.replace(/^\./, '').toLowerCase() === extension)?.[1] || 'application/octet-stream';
            const blob = b64toBlob(result.body.file, mimeType)
            saveAs(blob, fileName);
        }
        catch (error) {
            alert({ title: 'Error downloading document', body: JSON.stringify(error) });
        }
        finally {
            setGettingDocument(false);
        }
    }
    const uploadFile = useCallback(async (e?: unknown) => {
        const selectedFile = e as File;
        const extension = selectedFile?.name?.split('.').pop()?.toLowerCase();
        const allowedExtensions = uploadPolicy?.allowedExtensions?.map(value => value.replace(/^\./, '').toLowerCase()) || [];

        if (!uploadPolicy) {
            alert({ title: 'Error uploading document', body: <p>Upload requirements are unavailable. Please try again later.</p> });
            return;
        }
        if (!selectedFile?.name) {
            alert({ title: 'Error uploading document', body: <p>A file is required.</p> });
            return;
        }

        if (!extension || !allowedExtensions.includes(extension)) {
            alert({ title: 'Error uploading document', body: <p>This file type is not supported by the current upload policy.</p> });
            return;
        }

        if (selectedFile.size > uploadPolicy.maximumFileSizeBytes) {
            alert({ title: 'Error uploading document', body: <p>The file exceeds the configured maximum allowed size.</p> });
            return;
        }

        setUploading(true);
        try {
            const approved = false;
            const file64 = await blobToBase64(selectedFile);

            const file = file64.split(',')[1];
            const fileName = selectedFile.name;
            const newDoc = { ...doc, file, fileName, approved };

            const result = await fetch(endpoints.updateSupplierDocument + supplierId, { method: 'POST', body: JSON.stringify(newDoc) });
            const { body } = result;
            if (!body?.success) {
                alert({ title: 'Error uploading document', body: <div> <h6>{(body?.messages || []).map(x => x)}</h6></div> });
                setFile();
                return;
            }

            setFile();
            refresh && refresh();
        }
        catch (error) {
            alert({ title: "Error:", body: JSON.stringify(error) });
            setFile();
        }
        finally {
            setUploading(false);
        }
    }, [refresh, supplierId, alert, doc, fetch, uploadPolicy]);


    const cancelApprove = () => {
        setUploading(true)
        const approved = false;
        const approvedBy = '';
        const newDoc = { id: doc.id, fileCategory: doc.fileCategory, approved, approvedBy, file: null };
        fetch(endpoints.updateSupplierDocument + supplierId, { method: 'POST', body: JSON.stringify(newDoc) }).then((result) => {
            const { body } = result;
            if (!body.success) {
                alert({ title: 'Error cancelling approval', body: <div><h6>{body.messages.map(x => x)}</h6></div> });
            }
            refresh && refresh();
            setUploading(false);
        }, (error) => {
            alert({ title: 'Error', body: JSON.stringify(error) });
            setUploading(false);
        })
    };


    const approveDocument = () => {
        if (approving) {
            if (!expireDate || dayjs(expireDate).isBefore(dayjs(), 'day')) {
                return;
            }
            const approvedBy = '';
            const expiredAt = dayjs(expireDate).format('YYYY-MM-DDTHH:mm:ss');
            const newDoc = { id: doc.id, fileCategory: doc.fileCategory, approved: true, approvedBy, expiredAt, file: null };
            setUploading(true);
            fetch(endpoints.updateSupplierDocument + supplierId, { method: 'POST', body: JSON.stringify(newDoc) })
                .then((result) => {
                    const { body } = result;
                    if (!body.success) {
                        alert({ title: 'Error approving document', body: <div> <h6>{body.messages.map(x => x)}</h6></div> });
                    }

                    refresh && refresh();
                }, (error) => {
                    alert({ title: 'error', body: JSON.stringify(error) });
                })
                .finally(() => {
                    setUploading(false);
                });
            setApproving(false);
            setExpire();
        }
        else {
            setApproving(true);
        }


    }
    useEffect(() => {
        if (newFile) {
            uploadFile(newFile)
        }
    }, [newFile, uploadFile])
    const admin = auth.role === "Admin"
    const canViewApprovalStatus = auth.role === "Supplier" || admin
    const category = fileCategory.replace(/([A-Z]+)/g, " $1").replace(/([A-Z][a-z])/g, " $1")
    const hasCancelApproval = admin && approved && !approving;
    const hasInlineApprove = admin && !approved && !expired && !approving;
    const hasFile = Boolean(fileName);
    // Status drives the badge color and left-accent border class; optional documents use the same validity colors.
    const statusVariant = status === "Current" ? 'current' : status === "Nearly Expired" ? 'nearly-expired' : status === "Expired" ? 'expired' : status === "Not Checked" ? 'not-checked' : status === "Rejected" ? 'rejected' : optional ? 'optional' : 'expired';
    const badgeColor = statusVariant === 'current' ? 'success' : statusVariant === 'nearly-expired' ? 'warning' : statusVariant === 'expired' ? 'danger' : statusVariant === 'not-checked' ? 'info' : statusVariant === 'rejected' ? 'dark' : 'secondary';
    return <div
        className={`doc-card doc-card--${statusVariant}${hasCancelApproval ? ' doc-card--compact-bottom' : ''}`}
    >
        <div className="doc-card__body">
            <div className="doc-card__header">
                <h5 className="doc-card__title">{category}</h5>
                <Badge color={badgeColor} pill>{optional && !status ? 'Optional' : status}</Badge>
            </div>
            <div className="doc-card__content">
                <div className="doc-card__panel doc-card__main">
                    {doc.supplier ? <div className="doc-card__supplier-row"><SupplierDetail index={index} supplier={doc.supplier} route={route} /></div> : null}
                    <div className="doc-card__filename-row">
                        <div className="doc-card__filename">{fileName || <span className="doc-card__empty">No file uploaded</span>}</div>
                        {fileName ? <Button size="sm" className="doc-card__btn-icon-only" onClick={downloadDoc} disabled={gettingDocument} aria-label="Download">
                            {gettingDocument ? <Spinner size="sm" /> : <FontAwesomeIcon icon={faDownload} />}
                        </Button> : null}
                    </div>
                </div>
                {uploading ? <div className="doc-card__panel doc-card__side doc-card__uploading">
                    <Spinner size="sm" className="doc-card__btn-icon" />Uploading...
                </div> : <div className="doc-card__panel doc-card__side">
                    {fileName && !newFile ? <>
                        <div className="doc-card__meta-group">
                            {approving ? <div className="doc-card__meta doc-card__meta-inline">
                                <span>Expires:</span>
                                <DatePickerControl selected={expireDate} minDate={dayjs().startOf('day').toDate()} onChange={(e) => setExpire(e)} type='date' />
                                {admin && !approved && !expired ? <div className="doc-card__meta-inline-actions">
                                    <Button size="sm" color="success" onClick={approveDocument} disabled={!expireDate}>
                                        <FontAwesomeIcon className="doc-card__btn-icon" icon={faCheck} />Approve
                                    </Button>
                                    <Button size="sm" color="secondary" outline onClick={() => { setApproving(false); setExpire(); }}>
                                        Cancel
                                    </Button>
                                </div> : null}
                            </div>
                                : approved ? <div className="doc-card__approval-meta">
                                    <div className="doc-card__meta">
                                        {'Approved by: ' + (checkedByUser?.userName || '—')}
                                    </div>
                                    <div className="doc-card__meta">
                                        <span>{'Last Checked: ' + showDateTime(lastChecked) + ' | Expires: ' + showDate(expiredAt)}</span>
                                    </div>
                                    {hasCancelApproval ? <div className="doc-card__approval-actions">
                                        <Button size="sm" color="danger" outline onClick={cancelApprove}>
                                            <FontAwesomeIcon className="doc-card__btn-icon" icon={faBan} />Cancel Approval
                                        </Button>
                                    </div> : null}
                                    </div> : status === "Expired" ? <>
                                    <div className="doc-card__meta">
                                        {'Expired: ' + showDate(expiredAt)}
                                    </div>
                                </> : status === "Rejected" ? canViewApprovalStatus ? <div className="doc-card__meta doc-card__meta-empty doc-card__awaiting-row">
                                        <span>Approval cancelled. Please upload a replacement for review.</span>
                                        {hasInlineApprove ? <Button size="sm" color="success" onClick={approveDocument}>
                                            <FontAwesomeIcon className="doc-card__btn-icon" icon={faCheck} />Approve
                                        </Button> : null}
                                    </div> : null
                                    : canViewApprovalStatus ? <div className="doc-card__meta doc-card__meta-empty doc-card__awaiting-row">
                                        <span>Awaiting approval</span>
                                        {hasInlineApprove ? <Button size="sm" color="success" onClick={approveDocument}>
                                            <FontAwesomeIcon className="doc-card__btn-icon" icon={faCheck} />Approve
                                        </Button> : null}
                                    </div> : null
                                }
                                </div>
                    </> : null}
            </div>}
            {(auth.role === "Supplier" || admin) ? <div className="doc-card__dropzone-row">
                <UploadDropZone
                    accept={uploadPolicy?.allowedExtensions?.join(',')}
                    disabled={!uploadPolicy}
                    handleChange={uploadFile}
                    promptText={hasFile ? 'Drag a replacement here' : 'Drag a file here'}
                />
            </div> : null}
        </div>
    </div>
    </div>
}


export default DocumentDetails;